import { scaleLinear } from "d3-scale";
import { constant } from "fp-ts/lib/function";
import React from "react";
import { defineMessage, useIntl } from "react-intl";
import { LineChart } from "../charts/LineChart";
import { withFigureIO } from "../components/figure";
import { useConfig } from "../config";
import { metadata } from "../data/data_learning_fig_LEARN_AGE_GRADE";
import { datasets } from "../data/data_learning_fig_LEARN_AGE_GRADE_datasets";
import { getCountryName, mkGemDataDecoder, useFigureControlItems, useMultiSelectState } from "../domain";
import { useMedia } from "../hooks";
import { extentNumber, fromNewtype, unsafeFromArray } from "../lib";
import * as M from "../materials";
import { breakpoints } from "../materials/consts.stylex";
import { An, io, O, pipe, R } from "../prelude";
export * from "../data/data_learning_fig_LEARN_AGE_GRADE";
const DEFAULT_REGIONS = [
    "IND:Punjab",
    "PAK:Punjab",
    "PAK:Sindh"
];
const Index = io.keyof({
    age: null,
    grade: null
});
const indexLookup = {
    age: defineMessage({
        id: "fig.learning.LEARN_AGE_GRADE.age",
        defaultMessage: "Age"
    }),
    grade: defineMessage({
        id: "fig.learning.LEARN_AGE_GRADE.grade",
        defaultMessage: "Grade"
    })
};
export const Data = mkGemDataDecoder([
    "value"
], {
    index: Index,
    index_value: io.number,
    iso3c: fromNewtype(io.string),
    region: io.string
});
const mkKey = (x)=>`${x.iso3c}:${x.region}`;
export const Chart = ({ data: dataRaw })=>{
    const intl = useIntl();
    const env = useConfig();
    const screenSDown = !useMedia(breakpoints.s);
    const screenMDown = !useMedia(breakpoints.m);
    const [selectedRegions, actions] = useMultiSelectState("regions");
    const data = React.useMemo(()=>{
        return dataRaw.map((x)=>{
            const country_name = getCountryName(env.countries, x.iso3c);
            return {
                ...x,
                key: mkKey(x),
                country_name,
                full_name: `${country_name} – ${x.region}`,
                index_name: intl.formatMessage(indexLookup[x.index])
            };
        });
    }, [
        intl,
        env.countries,
        dataRaw
    ]);
    const regionControlItems = useFigureControlItems(data, (x)=>x.key, (x)=>x.full_name);
    React.useEffect(()=>{
        actions.setSelectionControl("regions", {
            type: "MultiSelect",
            label: intl.formatMessage(defineMessage({
                id: "Regions",
                defaultMessage: "Regions"
            })),
            selected: O.some(DEFAULT_REGIONS),
            maxItems: 3
        }, regionControlItems);
    }, [
        intl,
        actions,
        regionControlItems
    ]);
    const { chartData, lookupExtent } = React.useMemo(()=>{
        const lookupExtent_ = pipe(unsafeFromArray(data), An.groupBy((x)=>x.index), R.map((xs)=>extentNumber(xs, (x)=>x.index_value)), R.map((xs)=>scaleLinear().domain(xs).range([
                0,
                1
            ])));
        return {
            chartData: data.filter((x)=>pipe(selectedRegions, O.fold(()=>false, (xs)=>xs.includes(x.key)))).map((x)=>({
                    ...x,
                    normalized_value: lookupExtent_[x.index](x.index_value)
                })),
            lookupExtent: (x)=>lookupExtent_[x]
        };
    }, [
        data,
        selectedRegions
    ]);
    const columnCount = pipe(selectedRegions, O.fold(()=>1, (xs)=>xs.length));
    const isNarrow = screenMDown || columnCount > 3;
    return <LineChart tLabel={(s)=>s} height={screenSDown ? M.chartHeight.xs : screenMDown ? M.chartHeight.s : M.chartHeight.m} endLabel={!screenMDown} x="normalized_value" numberFormat=".0%" category="datum.index_name" colorLegend column="full_name" columns={columnCount} values={chartData} xAxisFormat={(x)=>{
        if (typeof x === "number") {
            const ageValue = lookupExtent("age").invert(x);
            const gradeValue = lookupExtent("grade").invert(x);
            return x > 0 && isNarrow ? `${ageValue} / ${gradeValue}` : `${intl.formatMessage(indexLookup["age"])} ${ageValue} / ${intl.formatMessage(indexLookup["grade"])} ${gradeValue}`;
        }
        return "";
    }} tooltipFormatX={constant("")} tooltipFormatTitle={(x)=>`${intl.formatMessage(indexLookup[x.index])} ${x.index_value}`} xScale="linear" showTooltip={"crosshair"}/>;
};
export default withFigureIO({
    url: new URL("../data/data_learning_fig_LEARN_AGE_GRADE.json", import.meta.url).href,
    csv: new URL("../data/data_learning_fig_LEARN_AGE_GRADE.zip", import.meta.url).href,
    xlsx: new URL("../data/data_learning_fig_LEARN_AGE_GRADE.xlsx", import.meta.url).href,
    metadata,
    Data,
    Chart,
    size: "wide",
    datasets
});
